export type Rule = (v: string) => boolean;
export type FileListRule = (f: FileList) => boolean;
export type ValidationRule = (errorMessage: string) => undefined | string;
export type Validator = (v: string | FileList) => string | undefined;

export const some =
  (...r: Rule[]) =>
  (v: string) =>
    !r.some(r => !r(v));
export const flSome =
  (...r: FileListRule[]) =>
  (f: FileList) =>
    !r.some(r => !r(f));
export const every =
  (...r: Rule[]) =>
  (v?: string) =>
    required(v) && r.every(r => r(v));
export const match = (r: RegExp) => (v: string | number) => r.test(v as string);
export const number = match(/^\d+$/);
type Args = string | (() => string);
export const rule =
  (s: Args, ...r: Rule[]) =>
  (v: string) =>
    some(...r)(v) ? undefined : typeof s === 'function' ? s() : s;
export const fileListRule =
  (s: Args, ...r: FileListRule[]) =>
  (v: FileList) =>
    flSome(...r)(v) ? undefined : typeof s === 'function' ? s() : s;
const isEmptyString = (v: string | undefined) => {
  return typeof v === 'string' && v.length === 0;
};
export const optional = (validator: Validator) => (v: string) => {
  if (v === undefined || isEmptyString(v)) {
    return undefined;
  }
  return validator(v);
};

export const boolTrue = (b: boolean | string): b is boolean => b === true;
export const required = (v?: string): v is string => v !== undefined && v.trim() !== '';
export const minValue = (n: number) => (v: string | number) =>
  (typeof v === 'string' ? parseInt(v, 10) : v) >= n;
export const maxValue = (n: number) => (v: string | number) =>
  (typeof v === 'string' ? parseInt(v, 10) : v) <= n;
export const minMaxValue = (nMin: number, nMax: number) => (v: string) =>
  every(minValue(nMin), maxValue(nMax))(v);

export const minLength =
  (n: number) =>
  (v = '') =>
    v.length <= n;
export const maxLength =
  (n: number) =>
  (v = '') =>
    v.length >= n;
export const minMaxLength = (nMin: number, nMax: number) => (v: string) =>
  every(minLength(nMin), maxLength(nMax))(v);

export const isValidEmail = match(
  // eslint-disable-next-line no-control-regex
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9]){1,}|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i
);
